import React, { useEffect, useState } from "react";
import { Table, Empty } from "antd";
import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import { API_URL } from "../../constants";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import useTableSearch from "../../hooks/useTableSearch";
import "./workingmonth.css";

const paginationProps = {
  defaultPageSize: 20,
  pageSizeOptions: ["10", "20", "50", "100"],
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (t, r) => `${r[0]}–${r[1]} din ${t} backup-uri`,
};

const Backups = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const token = useSelector((state) => state.user.currentUser?.accessToken);
  const getColumnSearchProps = useTableSearch();

  const getBackupsList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL + "user/gestionar/backups", {
        headers: { Authorization: "Bearer " + token },
      });
      const sorted = res.data.sort(
        (a, b) => new Date(b.createdDate) - new Date(a.createdDate)
      );
      setList(sorted);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBackupsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async (record) => {
    setDownloading(record.filename);
    try {
      const res = await axios.get(
        API_URL + "user/gestionar/download/backup/" + record.filename,
        {
          headers: { Authorization: "Bearer " + token },
          responseType: "blob",
        }
      );
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", record.filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    } finally {
      setDownloading(null);
    }
  };

  const latest = list.length ? list[0] : null;
  const latestAgeHours = latest
    ? dayjs().diff(dayjs(latest.createdDate), "hour")
    : null;
  const latestOk = latestAgeHours !== null && latestAgeHours < 26;

  const mainColumns = [
    {
      title: "Creat la data de",
      dataIndex: "createdDate",
      key: "createdDate",
      width: 220,
      sorter: (a, b) => new Date(a.createdDate) - new Date(b.createdDate),
      defaultSortOrder: "descend",
      render: (v) => (
        <span className="wm-log-time">
          {dayjs(v).format("DD.MM.YYYY, HH:mm:ss")}
        </span>
      ),
    },
    {
      title: "Nume fișier",
      dataIndex: "filename",
      key: "filename",
      sorter: (a, b) => (a.filename || "").localeCompare(b.filename || ""),
      render: (v) => <span className="wm-log-email">{v}</span>,
      ...getColumnSearchProps("filename"),
    },
    {
      title: "",
      key: "actions",
      width: 150,
      align: "right",
      render: (_, record) => (
        <button
          type="button"
          className={`wm-btn wm-btn-secondary wm-btn-sm${
            downloading === record.filename ? " wm-btn-loading" : ""
          }`}
          onClick={() => download(record)}
        >
          {downloading === record.filename ? (
            <span className="wm-spin" />
          ) : (
            <DownloadOutlined />
          )}
          Descarcă
        </button>
      ),
    },
  ];

  return (
    <div className="wm-page">
      {/* ===== SUMAR ===== */}
      <div className="wm-stats" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        <div className="wm-stat">
          <div className="wm-stat-label">Total backup-uri</div>
          <div className="wm-stat-value">{list.length}</div>
          <div className="wm-stat-meta">pe server</div>
        </div>
        <div className="wm-stat">
          <div className="wm-stat-label">Cel mai recent</div>
          <div className="wm-stat-value" style={{ fontSize: 20 }}>
            {latest ? dayjs(latest.createdDate).format("DD.MM.YYYY, HH:mm") : "—"}
          </div>
          <div className="wm-stat-meta">{latest ? latest.filename : ""}</div>
        </div>
        <div className={`wm-stat ${latestOk ? "wm-stat-good" : "wm-stat-bad"}`}>
          <div className="wm-stat-label">Vechime</div>
          <div className="wm-stat-value">
            {latestAgeHours !== null
              ? latestAgeHours < 24
                ? `${latestAgeHours} ore`
                : `${Math.floor(latestAgeHours / 24)} zile`
              : "—"}
          </div>
          <div className="wm-stat-meta">
            {latestOk
              ? "backup-urile rulează normal"
              : "atenție: ultimul backup e vechi!"}
          </div>
        </div>
      </div>

      {/* ===== TABEL ===== */}
      <div className="wm-card wm-history">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 14,
          }}
        >
          <h3 className="wm-card-title" style={{ margin: 0 }}>
            Backup-urile bazei de date
          </h3>
          <button
            type="button"
            className="wm-btn wm-btn-secondary wm-btn-sm"
            onClick={getBackupsList}
          >
            <ReloadOutlined /> Reîncarcă
          </button>
        </div>
        <Table
          columns={mainColumns}
          dataSource={list}
          rowKey="id"
          size="small"
          loading={loading}
          pagination={paginationProps}
          locale={{ emptyText: <Empty description={"Nu există backup-uri"} /> }}
        />
      </div>
    </div>
  );
};

export default Backups;
